import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Download, FileSpreadsheet, FileText, FileJson, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEPARTAMENTOS = ["ADMIN", "CLIMA", "CONS", "EST", "ETEL", "FONT/PCI", "FV", "PDS"];
const TIPO_LABELS = {
  AVERIA: "Avería",
  SERVICIO_PUNTUAL: "Servicio Puntual",
  OBRA: "Obra",
  MANTENIMIENTO: "Mantenimiento",
  VARIOS: "Varios",
};

export default function AdminExport() {
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [departamento, setDepartamento] = useState("");
  const [empleadoId, setEmpleadoId] = useState("");
  const [tipoServicio, setTipoServicio] = useState("");
  const [empleados, setEmpleados] = useState([]);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [loadingXlsx, setLoadingXlsx] = useState(false);
  const [loadingJson, setLoadingJson] = useState(false);
  const [holdedPreview, setHoldedPreview] = useState(null);

  useEffect(() => {
    api.get("/users/directory").then((r) => {
      setEmpleados((r.data || []).map((u) => ({ id: u.id, name: u.name || u.email })));
    }).catch(() => {});
  }, []);

  const buildParams = () => ({
    fecha_desde: fechaDesde || undefined,
    fecha_hasta: fechaHasta || undefined,
    departamento: departamento || undefined,
    empleado_id: empleadoId || undefined,
    tipo_servicio: tipoServicio || undefined,
  });

  const downloadBlob = async (path, filename, setBusy, okMsg) => {
    setBusy(true);
    try {
      const res = await api.get(path, { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar");
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const downloadCSV = () =>
    downloadBlob("/export/csv", `partes_suntelco_${today}.csv`, setLoadingCsv, "CSV descargado");
  const downloadXLSX = () =>
    downloadBlob("/export/xlsx", `partes_suntelco_${today}.xlsx`, setLoadingXlsx, "Excel descargado");

  const previewHolded = async () => {
    setLoadingJson(true);
    try {
      const { data } = await api.get("/export/holded", { params: buildParams() });
      setHoldedPreview(data);
    } catch {
      toast.error("Error obteniendo payload Holded");
    } finally {
      setLoadingJson(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="export-page">
      <header>
        <div className="label-up text-[#FF4500]">Solo admin</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">Exportar partes</h1>
        <p className="text-zinc-600 text-sm mt-1">
          Descarga los partes filtrados en Excel o CSV. Formato listo para leer:
          cabeceras en español, fechas DD/MM/AAAA y tipo de servicio con nombre completo.
        </p>
      </header>

      <section className="brutal-card p-5 space-y-4">
        <h2 className="font-display font-black text-xl">Filtros</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-up block mb-2">Fecha desde</label>
            <input data-testid="filter-from" type="date" className="brutal-input w-full"
              value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          </div>
          <div>
            <label className="label-up block mb-2">Fecha hasta</label>
            <input data-testid="filter-to" type="date" className="brutal-input w-full"
              value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-up block mb-2">Departamento</label>
            <select data-testid="filter-departamento" className="brutal-input w-full"
              value={departamento} onChange={(e) => setDepartamento(e.target.value)}>
              <option value="">Todos</option>
              {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-up block mb-2">Tipo de servicio</label>
            <select data-testid="filter-tipo" className="brutal-input w-full"
              value={tipoServicio} onChange={(e) => setTipoServicio(e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(TIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-up block mb-2">Empleado</label>
          <select data-testid="filter-empleado" className="brutal-input w-full"
            value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)}>
            <option value="">Todos los empleados</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          data-testid="download-xlsx"
          onClick={downloadXLSX}
          disabled={loadingXlsx}
          className="brutal-card p-5 text-left bg-emerald-600 text-white hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform disabled:opacity-70"
        >
          <div className="flex items-center justify-between">
            <FileSpreadsheet size={28} />
            {loadingXlsx ? <Loader2 className="animate-spin" /> : <Download size={20} />}
          </div>
          <div className="font-display font-black text-xl mt-3 leading-tight">Descargar Excel</div>
          <div className="text-xs uppercase tracking-wider mt-2 opacity-90">
            .xlsx con cabeceras, autofiltro y ancho ajustado
          </div>
        </button>
        <button
          data-testid="download-csv"
          onClick={downloadCSV}
          disabled={loadingCsv}
          className="brutal-card p-5 text-left bg-[#FF4500] text-white hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform disabled:opacity-70"
        >
          <div className="flex items-center justify-between">
            <FileText size={28} />
            {loadingCsv ? <Loader2 className="animate-spin" /> : <Download size={20} />}
          </div>
          <div className="font-display font-black text-xl mt-3 leading-tight">Descargar CSV</div>
          <div className="text-xs uppercase tracking-wider mt-2 opacity-90">
            Separado por ; con BOM UTF-8 (compatible con Excel en español)
          </div>
        </button>
        <button
          data-testid="preview-holded"
          onClick={previewHolded}
          disabled={loadingJson}
          className="brutal-card p-5 text-left hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform disabled:opacity-70"
        >
          <div className="flex items-center justify-between">
            <FileJson size={28} />
            {loadingJson ? <Loader2 className="animate-spin" /> : <Download size={20} />}
          </div>
          <div className="font-display font-black text-xl mt-3 leading-tight">Preview JSON (Holded API)</div>
          <div className="text-xs uppercase tracking-wider mt-2 text-zinc-600">
            Endpoint listo para tu integración API
          </div>
        </button>
      </section>

      {holdedPreview && (
        <section className="brutal-card p-5" data-testid="holded-preview-section">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-black text-lg">Payload Holded</h3>
            <span className="brutal-chip">{holdedPreview.count} items</span>
          </div>
          <pre className="mt-3 text-xs bg-[#09090B] text-[#FACC15] p-4 overflow-auto max-h-96 font-mono">
{JSON.stringify(holdedPreview, null, 2)}
          </pre>
          <div className="text-xs text-zinc-600 mt-2">
            Endpoint GET <code className="bg-zinc-100 px-1">{API}/export/holded</code> — usa este JSON para enviar a la API de Holded cuando proporciones la API key.
          </div>
        </section>
      )}
    </div>
  );
}
